import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent } from './ui/card';
import { ArrowRight, Send } from 'lucide-react';
import { applicationFormData } from '../data/mockData';

const ApplicationForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    instagramHandle: '',
    message: '',
    hasSold: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      hasSold: value
    }));
    if (errors.hasSold) {
      setErrors(prev => ({
        ...prev,
        hasSold: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.instagramHandle.trim()) {
      newErrors.instagramHandle = 'Instagram handle is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please tell us about your audience';
    }

    if (!formData.hasSold) {
      newErrors.hasSold = 'Please select an option';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Mock submission
      console.log('Form submitted:', formData);
      onSubmit(formData);
    }
  };

  return (
    <section id="application-form" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Apply for a Launch Partnership
          </h2>
          <p className="text-lg text-slate-600">
            Tell us about yourself and your audience. We review every application manually.
          </p>
        </div>

        <Card className="shadow-2xl border-slate-200">
          <CardContent className="p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <div>
                <Label htmlFor="fullName" className="text-base font-semibold text-slate-900">
                  Full Name *
                </Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`mt-2 ${errors.fullName ? 'border-red-500' : 'border-slate-300'}`}
                  placeholder="John Doe"
                />
                {errors.fullName && (
                  <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-base font-semibold text-slate-900">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`mt-2 ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
                  placeholder="you@example.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Instagram Handle */}
              <div>
                <Label htmlFor="instagramHandle" className="text-base font-semibold text-slate-900">
                  Instagram Handle *
                </Label>
                <Input
                  id="instagramHandle"
                  name="instagramHandle"
                  type="text"
                  value={formData.instagramHandle}
                  onChange={handleChange}
                  className={`mt-2 ${errors.instagramHandle ? 'border-red-500' : 'border-slate-300'}`}
                  placeholder="@yourusername"
                />
                {errors.instagramHandle && (
                  <p className="text-red-500 text-sm mt-1">{errors.instagramHandle}</p>
                )}
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-base font-semibold text-slate-900">
                  Tell us about your audience *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={`mt-2 min-h-[120px] ${errors.message ? 'border-red-500' : 'border-slate-300'}`}
                  placeholder="Share details about your audience, engagement, content niche, and what you're looking to build..."
                />
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              {/* Have you sold to your audience before? */}
              <div>
                <Label htmlFor="hasSold" className="text-base font-semibold text-slate-900">
                  Have you sold to your audience before? *
                </Label>
                <Select value={formData.hasSold} onValueChange={handleSelectChange}>
                  <SelectTrigger className={`mt-2 ${errors.hasSold ? 'border-red-500' : 'border-slate-300'}`}>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
                {errors.hasSold && (
                  <p className="text-red-500 text-sm mt-1">{errors.hasSold}</p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Submit Application
                <Send className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ApplicationForm;
